import React, { useMemo, useState } from "react";
import {
  FiEdit,
  FiMoreVertical,
  FiTrash2,
} from "react-icons/fi";
import RefreshIcon from '@mui/icons-material/Refresh';
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, IconButton, Menu, MenuItem, Tooltip, Button } from "@mui/material";
import useConfirmationDialog from "../../hooks/useConfirmationDialog";

export const RowActions = ({ row, handleEditOpen, openConfirmationDialog }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', gap: '20px', width: '100%' }}>
      <Tooltip title="Edit">
        <IconButton onClick={() => handleEditOpen(row.original)}>
          <FiEdit size={12} color='blue' />
        </IconButton>
      </Tooltip>
      <IconButton onClick={handleMenuOpen}>
        <FiMoreVertical size={14} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          onClick={() => {
            openConfirmationDialog(row.original.buyerId);
            handleMenuClose();
          }}
          sx={{ color: 'red' }}
        >
          <FiTrash2 size={12} style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

const BuyerListing = ({
  content,
  handleEditOpen,
  handleRemoveTruck,
  setPagination,
  refetch,
  setIsDrawerOpen,
  setColumnFilters
}) => {
  const { openConfirmationDialog, ConfirmationDialog } = useConfirmationDialog(handleRemoveTruck);

  const [sorting, setSorting] = useState([]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "category",
        header: "Category"
      },
      {
        accessorKey: "buyerName",
        header: "Name",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const truncatedValue = value?.length > 25 ? `${value.substring(0, 25)}...` : value;
          return (
            <Tooltip title={value} arrow>
              <span>{truncatedValue}</span>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "gstNo",
        header: "GST No",
      },
      {
        accessorKey: "panNo",
        header: "PAN No",
      },
      {
        accessorKey: "tanNo",
        header: "TAN No",
      },
      {
        accessorKey: "contactNo",
        header: "Contact No",
      },
      {
        accessorKey: "orderedQuantity",
        header: "Ordered Qty",
      },
      {
        accessorKey: "deliveredQuantity",
        header: "Delivered Qty",
      },
      {
        accessorKey: "address",
        header: "Address",
        Cell: ({ cell }) => {
          const value = cell.getValue();
          const truncatedValue = value?.length > 25 ? `${value.substring(0, 25)}...` : value;
          return (
            <Tooltip title={value} arrow>
              <span>{truncatedValue}</span>
            </Tooltip>
          );
        },
      }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: content || [],
    enableColumnPinning: true,
    enableSorting: false,
    enableGlobalFilter: false,
    enableDensityToggle: false,
    manualFiltering: true,
    manualPagination: true,
    desnity: 'comfortable',
    initialState: { density: "compact", pagination: { pageIndex: 0, pageSize: 20 }, columnPinning: { right: ['mrt-row-actions'] } },
    muiPaginationProps: {
      rowsPerPageOptions: [20, 25, 50, 100],
      showFirstButton: true,
      showLastButton: true,
      sx: {
        color: "primary"
      },
    },
    renderRowActions: ({ row, table }) => (
      <RowActions row={row} handleEditOpen={handleEditOpen} openConfirmationDialog={openConfirmationDialog} />
    ),
    onColumnFiltersChange: setColumnFilters,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: ({ table }) => (
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: '3px', width: '100%', marginLeft: '10px' }}>
        <Tooltip arrow title="Refresh Data">
          <IconButton onClick={refetch} >
            <RefreshIcon onClick={refetch} />
          </IconButton>
        </Tooltip>
        <Button size='medium' variant='contained' color='primary' onClick={() => setIsDrawerOpen(true)} >
          Add new Buyer
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => {
            table.resetColumnFilters(); // Clear individual column filters
          }}
        >
          Clear Filters
        </Button>
      </Box>

    ),
    paginationDisplayMode: "pages",
    enableEditing: true,
    muiTableBodyRowProps: ({ staticRowIndex }) => ({
      sx: {
        backgroundColor: staticRowIndex.index % 2 === 0 ? "white" : "grey.100",
      },
    }),
  });

  return (<>{ConfirmationDialog}<MaterialReactTable table={table} /></>);
};

export default BuyerListing;